import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface TopStoryCompactProps {
  story: ExploreStoryCard;
  exploreBase: string;
}

/**
 * Compact row card for the Top Stories sidebar.
 * Headline-only with square 1:1 thumbnail, rounded left only.
 */
export function TopStoryCompact({ story, exploreBase }: TopStoryCompactProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const materialLabel = MATERIAL_TYPE_LABELS[story.materialType];
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];
  const shouldOptimizeImage = story.coverImageUrl
    ? canOptimizeExploreImage({ src: story.coverImageUrl })
    : false;

  return (
    <Link
      className="hover:-translate-y-px flex flex-1 items-center gap-3.5 rounded-xl bg-[var(--explore-bg-card)] p-2 no-underline shadow-[var(--explore-card-shadow)] transition-all hover:shadow-[var(--explore-card-shadow-hover)] active:scale-[0.995]"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Thumbnail — fixed width, stretches to card height */}
      <div className="relative w-[96px] shrink-0 self-stretch overflow-hidden rounded-l-[10px] bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        {story.coverImageUrl && (
          <Image
            alt={story.headline}
            className="h-full w-full object-cover"
            fill
            sizes="96px"
            src={story.coverImageUrl}
            unoptimized={!shouldOptimizeImage}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h4
          className="line-clamp-2 text-balance font-semibold text-[1rem] text-[var(--explore-text-primary)] leading-[1.35]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h4>
        <p className="mb-1 line-clamp-2 text-[0.78rem] text-[var(--explore-text-secondary)] leading-[1.4]">
          {story.summary}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {materialLabel && (
            <span
              className={`inline-flex items-center rounded-sm px-2 py-0.5 font-bold text-[0.6rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
            >
              {materialLabel}
            </span>
          )}
          {story.categoryEmoji && (
            <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 text-[0.75rem]">
              {story.categoryEmoji}
            </span>
          )}
          {primaryTag && (
            <span className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-2 py-0.5 font-medium text-[0.72rem] text-[var(--explore-tag-text)]">
              {primaryTag.displayName}
            </span>
          )}
          <span className="text-[0.72rem] text-[var(--explore-text-muted)]">
            {story.timeAgo}
          </span>
        </div>
      </div>
    </Link>
  );
}
